/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: Scheduler.ts
 * Author: Austin Robinson
 * Date Created: 9/13/2022
 * Copyright (c) DrivingDoc 2022
 */
import { Interval } from '@flatten-js/interval-tree';
import { DateSelectArg } from '@fullcalendar/core';
import moment, { Moment } from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { CreateCoursePeriodsParams } from 'api/hooks/private/periods/CreateCoursePeriods.ts';
import { CalendarCoursePeriod } from 'api/hooks/private/periods/GetAllCoursePeriods.ts';

import { getFontColor } from '../Drive';
import EventTree from './EventTree.ts';

interface DriveLocation {
  value: string;
  label: string;
}

interface DriveType {
  value: string;
  label: string;
  duration: number;
  courseSectionId: string;
  courseId: string;
}

export interface SchedulingOptions {
  location: DriveLocation; // drive location
  driveType: DriveType; // drive type
  registrationOpen: Moment; // date to open registration
  registrationClose: number; // minutes before start time to close registration
}

export interface CreateCoursePeriodsOptions {
  course_id?: string;
  section_id?: string;
  duration: number;
  capacity: number;
  observationCapacity: number;
  waitlistCapacity: number;
  openRegistration: Moment;
  closeRegistration: number;
}

interface NewDrive extends CreateCoursePeriodsParams {
  id: string;
}

export default class Scheduler {
  private calendar; // store a reference to FullCalendar's API
  private visibleResources: Set<string>;
  private currentInterval: Interval;

  public driveTree: EventTree<CalendarCoursePeriod>; // interval tree for drives already scheduled
  public newDriveTree: EventTree<NewDrive>; // interval tree for drives to be scheduled
  public newDriveStack = new Array<NewDrive[]>(); // stack of drives to be scheduled

  constructor(calendar: any) {
    this.calendar = calendar;
    this.visibleResources = new Set();
    this.currentInterval = new Interval(0, 0);
    this.driveTree = new EventTree();
    this.newDriveTree = new EventTree();
  }

  setCurrentInterval(interval: Interval) {
    this.currentInterval = interval;
  }

  addExistingDrive(coursePeriod: CalendarCoursePeriod, calendarElement: HTMLElement) {
    // only add event to resource tree if it is not a behind the wheel course
    const isBTWPeriod = coursePeriod?.course_section?.course?.course_type === 'behind_the_wheel';
    this.driveTree.addNewEvent(coursePeriod, isBTWPeriod, calendarElement);
  }

  eventAllowed(
    interval: Interval,
    resourceID: string,
    eventDuration: number,
    maxConcurrency: number,
    checkResourceOverlapOnly = false,
  ) {
    // event duration in milliseconds
    const durationMilliseconds = eventDuration * 60000;

    // number of events that will fit in current selection
    const numberOfEvents = Math.ceil((interval.high - interval.low) / 60000 / eventDuration);
    // for each potential event, verify the maximum number of occurrences
    for (let i = 0; i < numberOfEvents; i += 1) {
      const newInterval = new Interval(
        interval.low + durationMilliseconds * i,
        interval.low + durationMilliseconds * (i + 1),
      );
      // const dt = this.driveTree.resources.search(newInterval);
      // const newDt = this.newDriveTree.resources.search(newInterval);
      const result = new Set<string>([
        ...this.driveTree.resources.search(newInterval),
        ...this.newDriveTree.resources.search(newInterval),
      ]);
      console.log(`Drive ${i} overlaps ${result.size} other resources drives`);
      console.log(result);

      // verify individual resource is not being overbooked
      const existingResourceEvents = this.driveTree.resourceEvents?.get(resourceID)?.search(newInterval) ?? [];
      const newResourceEvents = this.newDriveTree.resourceEvents?.get(resourceID)?.search(newInterval) ?? [];
      if (existingResourceEvents?.length > 0 || newResourceEvents?.length > 0) {
        console.log('Would Overbook Resource');
        return false;
      }

      if (!checkResourceOverlapOnly && result.size >= maxConcurrency) {
        console.log('Would Overbook Cars');
        return false;
      }
    }
    return true;
  }

  undo() {
    const eventsToUndo = this.newDriveStack.pop(); // get events to undo
    const failed = new Array<NewDrive>(); // array to store failed events
    if (eventsToUndo) {
      while (eventsToUndo.length > 0) {
        const eventToRemove = eventsToUndo.pop(); // get the next event to remove
        if (!eventToRemove) {
          // no more events
          break; // break out of loop
        }
        if (this.newDriveTree.removeEvent(eventToRemove.id)) {
          // if it was removed from the tree
          this.calendar.current.getApi().getEventById(eventToRemove.id).remove(); // remove it from the calendar
        } else {
          // if it couldn't be removed push to faled stack
          console.error('Failed to undo an event');
          failed.push(eventToRemove);
        }
      }
      if (failed.length > 0) {
        // if any events couldn't be removed push them back to the new drives stack
        this.newDriveStack.push(failed);
      }
    }
  }

  getVisibleResources() {
    const visible = new Set<string>();
    const mapper = (value: string) => {
      visible.add(value);
    };
    this.driveTree.resources.search(this.currentInterval, mapper);
    this.newDriveTree.resources.search(this.currentInterval, mapper);
    return visible;
  }

  getVisibleResourceEvents(resourceID: string) {
    return {
      // todo needs changed when we deal with more than drives
      existingEvents: this.driveTree.getEventsByResource(resourceID, this.currentInterval),
      newEvents: this.newDriveTree.getEventsByResource(resourceID, this.currentInterval),
    };
  }

  toggleResource(resourceID: string) {
    if (this.visibleResources.has(resourceID)) {
      this.visibleResources.delete(resourceID);
    } else {
      this.visibleResources.add(resourceID);
    }
    return this.visibleResources;
  }

  setVisibleResources(visibleResources: Set<string>) {
    if (visibleResources) {
      this.visibleResources = visibleResources;
    }
  }

  private addNewDrive(
    drive: NewDrive,
    courseType: 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online',
  ) {
    this.newDriveTree.addNewEvent(drive, courseType === 'behind_the_wheel');
  }

  createDrives(
    slotInfo: DateSelectArg,
    options: CreateCoursePeriodsOptions,
    courseType: 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online',
  ) {
    const start: Moment = moment(slotInfo.startStr);
    const end: Moment = moment(slotInfo.endStr);
    const numOfDrives: number = Math.ceil(Math.abs(Math.ceil((start.unix() - end.unix()) / 60)) / options.duration);
    const startTime: Moment = moment(start);
    const endTime: Moment = moment(start).add(options.duration, 'minutes');
    const newDrives = new Array<NewDrive>();

    for (let i = 0; i < numOfDrives; i += 1) {
      const newDrive: NewDrive = {
        id: uuidv4(),
        courseSectionId: options.section_id ?? '',
        start: startTime.toISOString(),
        end: endTime.toISOString(),
        openEnrollment: options.openRegistration.toISOString(),
        closeEnrollment: moment(startTime).subtract(options.closeRegistration, 'minutes').toISOString(),
        capacity: options.capacity,
        observationCapacity: options.observationCapacity,
        waitlistCapacity: options.waitlistCapacity,
        course_period_instructors: [
          {
            instructor_user_id: slotInfo.resource?.id as string,
          },
        ],
      };

      const calendarDrive = {
        id: newDrive.id!,
        isNewDrive: true,
        drive: newDrive,
        resourceId: slotInfo.resource!.id,
        backgroundColor: slotInfo.resource?.toPlainObject().extendedProps.instructor.calendar_color,
        borderColor: slotInfo.resource?.toPlainObject().extendedProps.instructor.calendar_color,
        start: startTime.toDate(),
        end: endTime.toDate(),
        title: slotInfo.resource!.title,
        textColor: getFontColor(
          slotInfo.resource?.toPlainObject().extendedProps.instructor.calendar_color,
          0.8,
        ) as string,
        dateLabel: startTime.format('MMMM Do, YYYY'),
        startLabel: startTime.format('h:mm a'),
        endLabel: endTime.format('h:mm a'),
      } as any;

      newDrives.push(newDrive);
      this.addNewDrive(newDrive, courseType);
      (this.calendar as any).current.getApi().addEvent(calendarDrive);
      startTime.add(options.duration, 'minutes');
      endTime.add(options.duration, 'minutes');
    }
    this.newDriveStack.push(newDrives);
  }

  async submitNewDrives(mutateAsyncFn: any) {
    console.log('Submitting New Drives');
    if (!mutateAsyncFn) {
      return false;
    }

    const drivesToSubmit = this.newDriveStack.flat();
    const result = await mutateAsyncFn(drivesToSubmit);

    if (result) {
      this.newDriveStack = [];
      this.newDriveTree.getAllEventIds().forEach((id: string) => {
        this.calendar.current.getApi().getEventById(id).remove(); // remove it from the calendar
      });
      this.newDriveTree = new EventTree();
      return result;
    }
  }
}
